import React from "react";
import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Checkbox, Typography, Button } from "@mui/material";
import { useSettings } from "../../../providers/settings-provider";
import Player from 'lottie-react';
import okAnimation from '../../../files/json/approved.json';

export interface ProvisionRow {
  id: string;
  imported: {
    date: string;
    amount: string;
    description: string;
    category: string;
    // Original CSV row (every column, unmapped) - carried through to the
    // import payload so it can be stored per-transaction.
    sourceData?: unknown;
  };
  provision?: {
    id: string;
    date: string;
    amount: string;
    description: string;
    category: string;
  };
  checked: boolean;
}

export interface ProvisionMatchTableProps {
  rows: ProvisionRow[];
  onCheck: (id: string, checked: boolean) => void;
  onCheckAll: (checked: boolean) => void;
  allChecked: boolean;
  forDuplicates?: boolean;
}

const ProvisionMatchTable: React.FC<ProvisionMatchTableProps> = ({ rows, onCheck, onCheckAll, allChecked, forDuplicates }) => {
  const { formatDate, formatAmount } = useSettings();

  const parseDate = (dateStr: string | any): Date | null => {
    if (!dateStr) return null;
    
    const s = String(dateStr).trim();
    if (!s || s === '-') return null;

    // Try native parsing first
    let d = new Date(s);
    if (!isNaN(d.getTime()) && d.getFullYear() >= 1900 && d.getFullYear() <= 2100) {
        return d;
    }

    d = new Date(s.replace(/-/g, '/'));
    if (!isNaN(d.getTime()) && d.getFullYear() >= 1900 && d.getFullYear() <= 2100) {
        return d;
    }

    const currentYear = new Date().getFullYear();
    
    const monthMap: { [key: string]: number } = {
        'jan': 0, 'january': 0,
        'feb': 1, 'february': 1,
        'mar': 2, 'march': 2,
        'apr': 3, 'april': 3,
        'may': 4,
        'jun': 5, 'june': 5,
        'jul': 6, 'july': 6,
        'aug': 7, 'august': 7,
        'sep': 8, 'september': 8,
        'oct': 9, 'october': 9,
        'nov': 10, 'november': 10,
        'dec': 11, 'december': 11
    };

    const patterns = [
        { regex: /^(\d{4})[/.-](\d{1,2})[/.-](\d{1,2})$/, handler: (m: RegExpMatchArray) => new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10)) },
        { regex: /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/, handler: (m: RegExpMatchArray) => {
            const day = parseInt(m[1], 10);
            const month = parseInt(m[2], 10) - 1;
            const year = parseInt(m[3], 10);
            const testDate = new Date(year, month, day);
            if (testDate.getDate() === day && testDate.getMonth() === month) {
                return testDate;
            }
            return new Date(year, day - 1, month + 1);
        }},
        { regex: /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2})$/, handler: (m: RegExpMatchArray) => {
            const day = parseInt(m[1], 10);
            const month = parseInt(m[2], 10) - 1;
            let year = parseInt(m[3], 10);
            year = year < 50 ? 2000 + year : 1900 + year;
            return new Date(year, month, day);
        }},
        { regex: /^(\d{4})[/.-](\d{1,2})[/.-]([A-Za-z]+)$/, handler: (m: RegExpMatchArray) => {
            const year = parseInt(m[1], 10);
            const day = parseInt(m[2], 10);
            const month = monthMap[m[3].toLowerCase()];
            return month !== undefined ? new Date(year, month, day) : null;
        }},
        { regex: /^(\d{4})[/.-]([A-Za-z]+)[/.-](\d{1,2})$/, handler: (m: RegExpMatchArray) => {
            const year = parseInt(m[1], 10);
            const month = monthMap[m[2].toLowerCase()];
            const day = parseInt(m[3], 10);
            return month !== undefined ? new Date(year, month, day) : null;
        }},
        { regex: /^(\d{1,2})\s+([A-Za-z]{3,})\s+(\d{4})$/, handler: (m: RegExpMatchArray) => {
            const day = parseInt(m[1], 10);
            const month = monthMap[m[2].toLowerCase()];
            const year = parseInt(m[3], 10);
            return month !== undefined ? new Date(year, month, day) : null;
        }},
        { regex: /^([A-Za-z]{3,})\s+(\d{1,2})(?:\s+(\d{4}))?$/, handler: (m: RegExpMatchArray) => {
            const month = monthMap[m[1].toLowerCase()];
            const day = parseInt(m[2], 10);
            const year = m[3] ? parseInt(m[3], 10) : currentYear;
            return month !== undefined ? new Date(year, month, day) : null;
        }},
        { regex: /^(\d{1,2})[/.-]([A-Za-z]{3,})$/, handler: (m: RegExpMatchArray) => {
            const day = parseInt(m[1], 10);
            const month = monthMap[m[2].toLowerCase()];
            return month !== undefined ? new Date(currentYear, month, day) : null;
        }},
        { regex: /^([A-Za-z]+)[/.-](\d{4})$/, handler: (m: RegExpMatchArray) => {
            const month = monthMap[m[1].toLowerCase()];
            const year = parseInt(m[2], 10);
            return month !== undefined ? new Date(year, month, 1) : null;
        }},
        { regex: /^([A-Za-z]+)[/.-](\d{2})$/, handler: (m: RegExpMatchArray) => {
            const month = monthMap[m[1].toLowerCase()];
            let year = parseInt(m[2], 10);
            year = year < 50 ? 2000 + year : 1900 + year;
            return month !== undefined ? new Date(year, month, 1) : null;
        }},
        { regex: /^(\d{1,2})[/.-](\d{4})$/, handler: (m: RegExpMatchArray) => {
            const month = parseInt(m[1], 10) - 1;
            const year = parseInt(m[2], 10);
            return new Date(year, month, 1);
        }},
        { regex: /^(\d{1,2})[/.-](\d{2})$/, handler: (m: RegExpMatchArray) => {
            const month = parseInt(m[1], 10) - 1;
            let year = parseInt(m[2], 10);
            year = year < 50 ? 2000 + year : 1900 + year;
            return new Date(year, month, 1);
        }},
        { regex: /^(\d{4})$/, handler: (m: RegExpMatchArray) => {
            const year = parseInt(m[1], 10);
            return new Date(year, 0, 1);
        }},
        { regex: /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})[T\s](\d{1,2}):(\d{1,2})(?::(\d{1,2}))?$/, handler: (m: RegExpMatchArray) => {
            const day = parseInt(m[1], 10);
            const month = parseInt(m[2], 10) - 1;
            const year = parseInt(m[3], 10);
            return new Date(year, month, day);
        }},
        { regex: /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2})[T\s](\d{1,2}):(\d{1,2})(?::(\d{1,2}))?$/, handler: (m: RegExpMatchArray) => {
            const day = parseInt(m[1], 10);
            const month = parseInt(m[2], 10) - 1;
            let year = parseInt(m[3], 10);
            year = year < 50 ? 2000 + year : 1900 + year;
            return new Date(year, month, day);
        }},
        { regex: /^(\d{4})[/.-](\d{1,2})[/.-](\d{1,2})[T\s](\d{1,2}):(\d{1,2})(?::(\d{1,2}))?$/, handler: (m: RegExpMatchArray) => {
            const year = parseInt(m[1], 10);
            const month = parseInt(m[2], 10) - 1;
            const day = parseInt(m[3], 10);
            return new Date(year, month, day);
        }},
        { regex: /^(\d{1,2})[/.-]([A-Za-z]{3,})[/.-](\d{4})$/, handler: (m: RegExpMatchArray) => {
            const day = parseInt(m[1], 10);
            const month = monthMap[m[2].toLowerCase()];
            const year = parseInt(m[3], 10);
            return month !== undefined ? new Date(year, month, day) : null;
        }},
        { regex: /^(\d{1,2})[/.-]([A-Za-z]{3,})[/.-](\d{2})$/, handler: (m: RegExpMatchArray) => {
            const day = parseInt(m[1], 10);
            const month = monthMap[m[2].toLowerCase()];
            let year = parseInt(m[3], 10);
            year = year < 50 ? 2000 + year : 1900 + year;
            return month !== undefined ? new Date(year, month, day) : null;
        }},
        { regex: /^([A-Za-z]+)\s+(\d{1,2}),?\s+(\d{4})$/, handler: (m: RegExpMatchArray) => {
            const month = monthMap[m[1].toLowerCase()];
            const day = parseInt(m[2], 10);
            const year = parseInt(m[3], 10);
            return month !== undefined ? new Date(year, month, day) : null;
        }},
        { regex: /^([A-Za-z]+)$/, handler: (m: RegExpMatchArray) => {
            const month = monthMap[m[1].toLowerCase()];
            return month !== undefined ? new Date(currentYear, month, 1) : null;
        }},
    ];

    for (const pattern of patterns) {
        const m = s.match(pattern.regex);
        if (m) {
            const result = pattern.handler(m);
            if (result && !isNaN(result.getTime()) && result.getFullYear() >= 1900 && result.getFullYear() <= 2100) {
                return result;
            }
        }
    }
    
    return null;
  };
  return (
    <Box position="relative">
    <TableContainer sx={{ maxHeight: 340, background: 'var(--color-container-background)' }}>
      {rows.length === 0 ? (
        <Box 
          sx={{ 
            color: 'var(--color-primary)',
            padding: 4,
            textAlign: 'center',
            minHeight: 75,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Player
              autoplay
              loop
              animationData={okAnimation}
              style={{ height: 160, width: 160 }}
          />
          <Typography sx={{ fontWeight: 500, fontSize: 16, fontStyle: 'italic' }}>
            {!forDuplicates ? "No matching provisions found" : "No duplicate transactions found"}
          </Typography>
        </Box>
      ) : (
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox"></TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Amount</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
            {!forDuplicates && <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, index) => (
            <React.Fragment key={row.id}>
              <TableRow sx={{ background: index % 2 === 0 ? 'var(--color-container-background)' : '#fff' }}>
                <TableCell padding="checkbox" rowSpan={row.provision ? 2 : 1} sx={{ verticalAlign: 'center' }}>
                  <Checkbox checked={row.checked} onChange={e => onCheck(row.id, e.target.checked)} size="small"/>
                </TableCell>
                <TableCell>From Import File</TableCell>
                <TableCell>{formatDate(parseDate(row.imported.date) || new Date())}</TableCell>
                <TableCell>{formatAmount?.(Number(row.imported.amount))}</TableCell>
                <TableCell>{row.imported.description}</TableCell>
                {!forDuplicates && <TableCell>{row.imported.category}</TableCell>}
              </TableRow>
              {row.provision && (
                <TableRow sx={{ background: index % 2 === 0 ? 'var(--color-container-background)' : '#fff', mb: 4 }}>
                  <TableCell sx={{ color: forDuplicates ? 'var(--color-primary)' : '#e14ca6', fontWeight: 600 }}>{forDuplicates ? 'Transaction' : 'Provision'}</TableCell>
                  <TableCell sx={{ color: forDuplicates ? 'var(--color-primary)' : '#e14ca6', fontWeight: 600 }}>{formatDate(parseDate(row.provision.date) || new Date())}</TableCell>
                  <TableCell sx={{ color: forDuplicates ? 'var(--color-primary)' : '#e14ca6', fontWeight: 600 }}>{formatAmount?.(Number(row.provision.amount))}</TableCell>
                  <TableCell sx={{ color: forDuplicates ? 'var(--color-primary)' : '#e14ca6', fontWeight: 600 }}>{row.provision.description}</TableCell>
                  {!forDuplicates && <TableCell sx={{ color: '#e14ca6', fontWeight: 600 }}>{row.provision.category}</TableCell>}
                </TableRow>
              )}
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
      )}
    </TableContainer>
    <Box 
      sx={{ 
        position: 'absolute', 
        bottom: -65, 
        left: 8, 
        background: 'rgba(255, 255, 255, 0.8)', 
        borderRadius: 2,
        display: rows.length === 0 ? 'none' : 'block'
      }}
    >
      <Checkbox checked={allChecked} onChange={e => onCheckAll(e.target.checked)} size="small"/>
      {forDuplicates ? 'Keep All' : 'Approve All'}
    </Box>
    </Box>
  );
};

export default ProvisionMatchTable;
